import React, { useState, useEffect } from 'react';
import { AlertOctagon, Wallet, RefreshCw, ChevronRight, Loader2, ArrowUpCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * ReinstatementBanner
 *
 * Shown when the logged-in user has status.account === 'banned'
 * AND status.bannedReason === 'repeated_commission_default'.
 *
 * It shows their debt breakdown, reinstatement fee, and allows them
 * to pay directly from their wallet (or top up first).
 *
 * Usage: render this at the top of the Dashboard layout or as a
 * full-screen gate when the account is banned.
 */
const ReinstatementBanner = ({ user, walletBalance = 0, onReinstatementSuccess }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.get('/reinstatement/info');
        if (res.data.success) setInfo(res.data.data);
      } catch (err) {
        console.error('[ReinstatementBanner] Could not load reinstatement info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, []);

  const handlePay = async () => {
    if (!info) return;
    setPaying(true);
    try {
      const res = await api.post('/reinstatement/pay');
      if (res.data.success) {
        toast.success('Account reinstated! Welcome back to GigShip.');
        onReinstatementSuccess?.();
      } else {
        toast.error(res.data.message || 'Payment failed. Please try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment error. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const handleTopUp = () => navigate('/dashboard/wallet?action=topup');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="animate-spin text-red-500" />
      </div>
    );
  }

  const totalRequired = info?.breakdown?.totalRequired || 0;
  const hasFunds = walletBalance >= totalRequired;
  const shortfall = Math.max(0, parseFloat((totalRequired - walletBalance).toFixed(2)));

  return (
    <div
      className="min-h-screen flex items-start justify-center p-6 pt-16"
      style={{ background: '#fef2f2' }}
    >
      <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-xl">
        {/* ── Header ── */}
        <div style={{ background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)', padding: '28px 28px 22px' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/10 rounded-xl">
              <AlertOctagon size={22} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Account Suspended</h1>
          </div>
          <p className="text-red-100 text-sm leading-relaxed">
            Your account has been suspended due to repeated unpaid commission obligations. 
            Pay the reinstatement fee below to restore full access immediately.
          </p>
        </div>

        <div className="bg-white p-6 space-y-5">
          {info ? (
            <>
              {/* ── Debt Breakdown ── */}
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 space-y-2 text-sm">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Outstanding Commissions
                </p>
                {info.unpaidOrders?.map((o, i) => (
                  <div key={i} className="flex justify-between text-gray-600">
                    <span className="truncate max-w-[65%]">{o.gigTitle || o.orderNumber}</span>
                    <span className="font-semibold text-gray-800">GH₵ {(o.commissionOwed || 0).toFixed(2)}</span>
                  </div>
                ))}
                {info.breakdown?.otherFeesAndPenalties > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Other fees & penalties</span>
                    <span className="font-semibold text-gray-800">GH₵ {info.breakdown.otherFeesAndPenalties.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 flex justify-between text-gray-700">
                  <span>Total Debt</span>
                  <span className="font-bold">GH₵ {info.breakdown.totalDebt.toFixed(2)}</span>
                </div>
              </div>

              {/* ── Reinstatement Fee ── */}
              <div className="rounded-xl bg-red-50 border border-red-100 p-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Debt to clear</span>
                  <span className="font-semibold">GH₵ {info.breakdown.totalDebt.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Reinstatement penalty</span>
                  <span className="font-semibold text-red-700">+ GH₵ {info.breakdown.penalty.toFixed(2)}</span>
                </div>
                <p className="text-xs text-gray-400 italic">{info.breakdown.penaltyNote}</p>
                <div className="border-t border-red-100 pt-2 flex justify-between font-bold text-red-800 text-base">
                  <span>Total to Pay</span>
                  <span>GH₵ {info.breakdown.totalRequired.toFixed(2)}</span>
                </div>
              </div>

              {/* ── Wallet Balance ── */}
              <div
                className="rounded-xl border-2 p-4 flex items-center justify-between"
                style={{
                  borderColor: hasFunds ? '#16a34a' : '#dc2626',
                  background: hasFunds ? '#f0fdf4' : '#fef2f2'
                }}
              >
                <div className="flex items-center gap-3">
                  <Wallet size={20} style={{ color: hasFunds ? '#16a34a' : '#dc2626' }} />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Your Wallet</p>
                    <p className="text-xs" style={{ color: hasFunds ? '#15803d' : '#dc2626' }}>
                      GH₵ {walletBalance.toFixed(2)}{!hasFunds && ` · Short by GH₵ ${shortfall.toFixed(2)}`}
                    </p>
                  </div>
                </div>
                {hasFunds && <CheckCircle size={18} className="text-green-600" />}
              </div>

              {/* ── Action ── */}
              {hasFunds ? (
                <button
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full py-4 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
                  style={{ background: paying ? '#fca5a5' : 'linear-gradient(135deg,#dc2626,#7f1d1d)' }}
                >
                  {paying
                    ? <><Loader2 size={18} className="animate-spin" /> Processing…</>
                    : <><RefreshCw size={16} /> Reinstate Account — GH₵ {info.breakdown.totalRequired.toFixed(2)}</>
                  }
                </button>
              ) : (
                <button
                  onClick={handleTopUp}
                  className="w-full py-4 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)' }}
                >
                  <ArrowUpCircle size={16} />
                  Top Up Wallet (Need GH₵ {shortfall.toFixed(2)} more)
                </button>
              )}

              <button
                onClick={() => navigate('/contact')}
                className="w-full text-center text-sm text-red-600 hover:underline flex items-center justify-center gap-1"
              >
                Contact Support <ChevronRight size={14} />
              </button>

              <p className="text-center text-xs text-gray-400 leading-relaxed">
                Safety-related bans are permanent and cannot be reinstated. Financial reinstatement
                is only available for commission default. Payment is non-refundable.
              </p>
            </>
          ) : (
            <div className="py-8 text-center text-gray-400 text-sm">
              Could not load reinstatement details. Please refresh or contact support.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReinstatementBanner;
